import React, { useCallback, useEffect, useRef, useState } from 'react';
import Layout from '../components/global/layout';
import SEO from '../components/seo';
import PageBanner from '../components/layouts/page-banner';
import {
  Box,
  Container,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import LightMode from '../components/global/light-mode';
import VerticalPadding from '../components/global/vertical-padding';
import theme from '../lib/theme';
import CallToAction from '../components/layouts/call-to-action';
import Button from '../components/mui/button';

type BillingPeriod = 'monthly' | 'annual';

type PricingPlan = {
  name: string;
  monthlyPrice: string;
  annualPrice: string;
  monthlyHours?: number;
  description: string;
  featuresHeading: string;
  inheritsFrom?: string;
  features: string[];
  variant: 'standard' | 'enterprise';
};

const parsePrice = (price: string): number =>
  Number(price.replace(/[$,]/g, ''));

const formatSavings = (monthlyPrice: string, annualPrice: string): string | null => {
  const savings = (parsePrice(monthlyPrice) - parsePrice(annualPrice)) * 12;
  if (savings <= 0) {
    return null;
  }
  return `$${savings.toLocaleString('en-US')}`;
};

const pricingPlans: PricingPlan[] = [
  {
    name: 'Essentials',
    monthlyPrice: '$1,650',
    annualPrice: '$1,485',
    monthlyHours: 10,
    description:
      'Keep your website current with a responsive team ready to deliver updates, fixes, and improvements with standard priority.',
    featuresHeading: 'Key features:',
    features: [
      'Prompt delivery',
      'Email support',
      'Ongoing development',
      'Design enhancements',
      'SEO & accessibility standards',
    ],
    variant: 'standard',
  },
  {
    name: 'Growth',
    monthlyPrice: '$2,935',
    annualPrice: '$2,640',
    monthlyHours: 20,
    description:
      'Move faster with elevated priority and ongoing improvements that keep your website moving forward.',
    featuresHeading: 'Key features:',
    inheritsFrom: 'Essentials',
    features: [
      'Expedited delivery',
      'Slack collaboration',
      'Website growth planning',
      'Proactive technical SEO',
    ],
    variant: 'standard',
  },
  {
    name: 'Premium',
    monthlyPrice: '$4,890',
    annualPrice: '$4,400',
    monthlyHours: 40,
    description:
      'Accelerate growth with conversion-focused work, greater development capability, and priority execution.',
    featuresHeading: 'Key features:',
    inheritsFrom: 'Growth',
    features: [
      'Priority delivery',
      'JIRA & Monday.com collaboration',
      'Full website designs in Figma',
      'Custom development',
      'Performance optimization',
    ],
    variant: 'standard',
  },
  {
    name: 'Enterprise',
    monthlyPrice: '$8,000',
    annualPrice: '$8,000',
    description:
      'Put your roadmap first with a dedicated strategic partner to plan, prioritize, and accelerate your digital initiatives.',
    featuresHeading: 'Exclusive features:',
    inheritsFrom: 'Premium',
    features: [
      'First in line delivery',
      'Two-week sprint planning',
      'Dedicated strategic lead',
      'Conversion optimization (CRO)',
      'Reserved development capacity',
    ],
    variant: 'enterprise',
  },
];

const CheckIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M0 8C0 3.58172 3.58172 0 8 0C12.4183 0 16 3.58172 16 8C16 12.4183 12.4183 16 8 16C3.58172 16 0 12.4183 0 8Z"
      fill={color}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.6667 5.23654L6.73873 11.5L3.33333 8.26173L3.78152 7.83553L6.71447 10.6246L12.1955 4.83333L12.6667 5.23654Z"
      fill="black"
    />
  </svg>
);

const InheritArrowIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7.78 12.53C7.63937 12.6705 7.44875 12.7493 7.25 12.7493C7.05125 12.7493 6.86063 12.6705 6.72 12.53L2.47 8.28C2.32955 8.13937 2.25066 7.94875 2.25066 7.75C2.25066 7.55125 2.32955 7.36063 2.47 7.22L6.72 2.97C6.86226 2.83759 7.05031 2.76549 7.24463 2.76884C7.43895 2.7722 7.6244 2.85076 7.762 2.988C7.89924 3.1256 7.9778 3.31105 7.98116 3.50537C7.98451 3.69969 7.91241 3.88774 7.78 4.03L4.81 7H12.25C12.4489 7 12.6397 7.07902 12.7803 7.21967C12.921 7.36032 13 7.55109 13 7.75C13 7.94891 12.921 8.13968 12.7803 8.28033C12.6397 8.42098 12.4489 8.5 12.25 8.5H4.81L7.78 11.47C7.92045 11.6106 7.99934 11.8012 7.99934 12C7.99934 12.1988 7.92045 12.3894 7.78 12.53Z"
      fill="rgba(255,255,255,0.7)"
    />
  </svg>
);

const CarePlanCheckIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24.071"
    height="18.624"
    viewBox="0 0 24.071 18.624"
    role="img"
    aria-label="Included"
    focusable="false"
  >
    <g transform="translate(-875.338 -2110.351)">
      <rect
        width="4.159"
        height="11.862"
        transform="translate(886.667 2126.034) rotate(135)"
        fill="#8d81d3"
      />
      <rect
        width="4.159"
        height="22.179"
        transform="translate(883.726 2128.975) rotate(-135)"
        fill="#8d81d3"
      />
    </g>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  heading: {
    color: theme.palette.primary.dark,
  },
  cardsSection: {
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  cardsScroll: {
    display: 'flex',
    gap: 16,
    justifyContent: 'flex-start',
    overflowX: 'auto',
    overflowY: 'hidden',
    overscrollBehaviorX: 'contain',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    // Match MUI Container gutters so the first card lines up with the logo,
    // while the scroll area bleeds to the viewport edges.
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    [theme.breakpoints.up('md')]: {
      paddingLeft: 120,
      paddingRight: 120,
    },
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&:focus': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
  },
  cardsScrollCentered: {
    justifyContent: 'center',
  },
  cardsScrollIndicator: {
    boxSizing: 'border-box',
    marginTop: theme.spacing(4),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      paddingLeft: 120,
      paddingRight: 120,
    },
  },
  cardsScrollTrack: {
    backgroundColor: 'rgba(232, 231, 234, 0.16)',
    borderRadius: 99,
    height: 4,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  cardsScrollThumb: {
    backgroundColor: '#1C1B28',
    borderRadius: 99,
    height: '100%',
    position: 'absolute',
    top: 0,
  },
  cardsScrollHint: {
    color: '#9998A5',
    display: 'none',
    fontSize: 13,
    fontWeight: 500,
    marginTop: theme.spacing(1.5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    textAlign: 'left',
    [theme.breakpoints.up('md')]: {
      paddingLeft: 120,
      paddingRight: 120,
    },
    [theme.breakpoints.down('md')]: {
      display: 'block',
    },
  },
  card: {
    backgroundColor: '#1C1B28',
    border: '1px solid rgba(232, 231, 234, 0.16)',
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    flex: '0 0 350px',
    gap: 24,
    padding: 32,
    transition: 'opacity 0.4s ease',
    width: 350,
  },
  cardEnterprise: {
    border: '4px solid #8d81d3',
  },
  cardUnavailable: {
    opacity: 0.4,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    // Keep headers even when the annual label / hours badge appear or hide.
    minHeight: 110,
    width: '100%',
  },
  planName: {
    color: theme.palette.common.white,
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 32,
    fontWeight: 700,
    lineHeight: 1.2,
    margin: 0,
  },
  savingsLabel: {
    color: '#00cda0',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    margin: 0,
    minHeight: 15,
    transition: 'opacity 0.35s ease',
  },
  priceRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minHeight: 40,
    width: '100%',
  },
  priceLine: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    minHeight: 30,
    width: '100%',
  },
  priceSwap: {
    display: 'grid',
    minHeight: 30,
  },
  price: {
    color: theme.palette.common.white,
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 24,
    fontWeight: 700,
    gridArea: '1 / 1',
    lineHeight: 1.25,
    margin: 0,
    transition: 'opacity 0.35s ease',
  },
  priceHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  priceSuffix: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 20,
    fontWeight: 400,
  },
  hoursBadge: {
    backgroundColor: '#f9c82b',
    borderRadius: 99,
    color: '#1c1b28',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.2,
    padding: '2px 8px',
    transition: 'background-color 0.35s ease, color 0.35s ease, filter 0.35s ease',
    whiteSpace: 'nowrap',
  },
  hoursBadgeAnnual: {
    backgroundColor: 'rgba(232, 231, 234, 0.2)',
    color: 'rgba(255, 255, 255, 0.45)',
    filter: 'grayscale(1)',
  },
  hoursBadgeStrong: {
    fontWeight: 700,
  },
  hoursStruck: {
    textDecoration: 'line-through',
  },
  fadeHidden: {
    opacity: 0,
    pointerEvents: 'none',
  },
  description: {
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 400,
    lineHeight: 1.25,
    // Match the tallest description (~4 lines) so "Key features" aligns.
    minHeight: 80,
    margin: 0,
  },
  featuresHeadingBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
  },
  featuresHeading: {
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.25,
    margin: 0,
  },
  inheritsFrom: {
    alignItems: 'center',
    color: 'rgba(255, 255, 255, 0.7)',
    display: 'flex',
    fontSize: 12,
    fontWeight: 400,
    gap: 8,
    lineHeight: 1,
    margin: 0,
  },
  featureList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    listStyle: 'none',
    margin: 0,
    padding: 0,
    width: '100%',
  },
  featureItem: {
    alignItems: 'center',
    color: theme.palette.common.white,
    display: 'flex',
    fontSize: 14,
    fontWeight: 400,
    gap: 8,
    lineHeight: 1.2,
    margin: 0,
  },
  featureIcon: {
    flexShrink: 0,
    height: 16,
    width: 16,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0, 0, 0, 0)',
    clipPath: 'inset(50%)',
    height: 1,
    left: 0,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 0,
    whiteSpace: 'nowrap',
    width: 1,
  },
  footnoteLink: {
    color: '#fff',
    textDecoration: 'underline',
    '&:focus': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
  },
  tableTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 40,
    fontWeight: 600,
    lineHeight: 1.1,
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: 28,
      marginBottom: theme.spacing(3),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  cardsToolbar: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    justifyContent: 'center',
    marginBottom: theme.spacing(5),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      paddingLeft: 120,
      paddingRight: 120,
    },
  },
  billingNote: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.2,
    margin: 0,
    marginTop: 4,
    minHeight: 17,
    textAlign: 'center',
    transition: 'opacity 0.35s ease',
  },
  toggle: {
    alignItems: 'center',
    backgroundColor: 'rgba(232, 231, 234, 0.16)',
    borderRadius: 99,
    display: 'inline-flex',
    padding: 4,
    width: 'fit-content',
  },
  toggleOption: {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    borderRadius: 99,
    color: 'rgba(255, 255, 255, 0.7)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.2,
    margin: 0,
    padding: '2px 16px',
    transition: 'background-color 0.35s ease, color 0.35s ease',
    '&:focus': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
    '&:focus:not(:focus-visible)': {
      outline: 'none',
    },
    '&:focus-visible': {
      outline: '2px solid #6AD4E2',
      outlineOffset: 2,
    },
  },
  toggleOptionActive: {
    backgroundColor: '#8d81d3',
    color: theme.palette.common.white,
  },
  maintenanceSection: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(8),
    maxWidth: '100%',
    overflowX: 'hidden',
    paddingTop: theme.spacing(6),
  },
  maintenanceInner: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(4),
    justifyContent: 'center',
    maxWidth: '100%',
    width: 'fit-content',
  },
  maintenanceIntro: {
    boxSizing: 'border-box',
    maxWidth: 420,
    minWidth: 0,
    width: '100%',
    [theme.breakpoints.up('md')]: {
      flex: '0 1 420px',
      width: 420,
    },
  },
  maintenanceFeaturesCol: {
    boxSizing: 'border-box',
    flex: '0 1 auto',
    maxWidth: '100%',
    minWidth: 0,
    // Sized by the longest bullet; footnote wraps to match.
    width: 'max-content',
  },
  maintenanceTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 40,
    fontWeight: 600,
    lineHeight: 1.1,
    marginBottom: theme.spacing(2),
  },
  maintenancePrice: {
    color: theme.palette.common.white,
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 28,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  maintenanceDescription: {
    color: '#F2F2F7',
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.5,
    marginBottom: theme.spacing(4),
  },
  maintenanceFeatureList: {
    listStyle: 'none',
    margin: 0,
    maxWidth: '100%',
    padding: 0,
    width: 'max-content',
  },
  maintenanceFeatureItem: {
    alignItems: 'flex-start',
    color: '#F2F2F7',
    display: 'flex',
    fontSize: 14,
    fontWeight: 500,
    gap: theme.spacing(1.5),
    lineHeight: 1.4,
    marginBottom: theme.spacing(1.5),
    maxWidth: '100%',
    minWidth: 0,
    width: 'max-content',
  },
  maintenanceFeatureIcon: {
    flexShrink: 0,
    marginTop: 2,
  },
  maintenanceNote: {
    color: '#F2F2F7',
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: theme.spacing(3),
    minWidth: '100%',
    width: 0,
  },

  spacer: {
    [theme.breakpoints.down('md')]: {
      minHeight: 40,
    },
  },
  vertLine: {
    borderLeft: '1px solid #7D74BD',
    height: 400,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

const maintenanceFeatures = [
  '24/7 uptime monitoring',
  'Incident response when issues arise',
  'Weekly WordPress core and plugin updates',
  'Advanced page caching',
  'Global CDN',
  'Automated daily backups',
  'Enterprise-level security',
  'Managed hosting with one Pantheon Basic plan included',
];

const Pricing: React.FC = () => {
  const classes = useStyles();
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annual');
  const cardsScrollRef = useRef<HTMLDivElement>(null);
  const [scrollMetrics, setScrollMetrics] = useState({
    thumbWidth: 100,
    thumbOffset: 0,
    canScroll: false,
  });

  const updateScrollIndicator = useCallback(() => {
    const el = cardsScrollRef.current;
    if (!el) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const canScroll = maxScroll > 1;
    const thumbWidth = canScroll
      ? Math.max((clientWidth / scrollWidth) * 100, 12)
      : 100;
    const thumbOffset = canScroll
      ? (scrollLeft / maxScroll) * (100 - thumbWidth)
      : 0;

    setScrollMetrics({ thumbWidth, thumbOffset, canScroll });
  }, []);

  useEffect(() => {
    updateScrollIndicator();
    const el = cardsScrollRef.current;
    if (!el) {
      return;
    }

    const onScroll = () => updateScrollIndicator();
    // Browsers often convert vertical wheel input into horizontal scrolling on
    // overflow-x containers. Forward dominant vertical gestures to the page.
    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
        return;
      }

      event.preventDefault();
      window.scrollBy({ top: event.deltaY, left: 0, behavior: 'auto' });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('resize', updateScrollIndicator);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateScrollIndicator)
        : null;
    resizeObserver?.observe(el);

    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', updateScrollIndicator);
      resizeObserver?.disconnect();
    };
  }, [updateScrollIndicator]);

  return (
    <Layout>
      <SEO
        title="Our Monthly Web Operations Plans and Pricing"
        description="Our monthly website development pricing structure is built on a clear and predictable pricing model and monthly fees."
      />
      <PageBanner
        heading="Keep your costs predictable and your options open"
        imageSrc="pricing-banner.jpg"
      />
      <LightMode>
        <VerticalPadding>
          <Container>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
              spacing={4}
            >
              <Grid item xs={12} md={5}>
                <Typography
                  variant="h2"
                  className={classes.heading}
                  gutterBottom
                >
                  get the plan that works for you
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" component="p">
                  Every plan includes the same experienced team for design,
                  development, fixes, and improvements, with small updates often
                  shipped the same day. As you move up the tiers, you&apos;ll
                  receive higher queue priority, more collaboration, and access
                  to redesigns, CRO, and larger development initiatives.
                </Typography>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </LightMode>
      <Box className={classes.cardsSection}>
        <VerticalPadding>
          <Container maxWidth={false}>
            <Typography
              id="pricing-table-heading"
              component="h2"
              className={classes.tableTitle}
            >
              Simple pricing. Long-term partnership.
            </Typography>
          </Container>
          <div className={classes.cardsToolbar}>
            <div
              className={classes.toggle}
              role="group"
              aria-label="Billing period"
            >
              <button
                type="button"
                className={`${classes.toggleOption}${
                  billingPeriod === 'monthly'
                    ? ` ${classes.toggleOptionActive}`
                    : ''
                }`}
                aria-pressed={billingPeriod === 'monthly'}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`${classes.toggleOption}${
                  billingPeriod === 'annual'
                    ? ` ${classes.toggleOptionActive}`
                    : ''
                }`}
                aria-pressed={billingPeriod === 'annual'}
                onClick={() => setBillingPeriod('annual')}
              >
                Annual
              </button>
            </div>
            <p
              className={`${classes.billingNote}${
                billingPeriod === 'monthly' ? ` ${classes.fadeHidden}` : ''
              }`}
              aria-hidden={billingPeriod === 'monthly' || undefined}
            >
              Annual, billed monthly
            </p>
          </div>
          <div
            ref={cardsScrollRef}
            className={`${classes.cardsScroll}${
              scrollMetrics.canScroll ? '' : ` ${classes.cardsScrollCentered}`
            }`}
            role="region"
            aria-labelledby="pricing-table-heading"
            tabIndex={0}
          >
            {pricingPlans.map((plan) => {
              const isEnterprise = plan.variant === 'enterprise';
              const isMonthly = billingPeriod === 'monthly';
              const isAnnual = !isMonthly;
              const annualSavings = formatSavings(
                plan.monthlyPrice,
                plan.annualPrice
              );
              const showSavings = isAnnual && Boolean(annualSavings);
              const checkColor = isEnterprise ? '#8d81d3' : '#00cda0';
              const isUnavailable = isEnterprise && isMonthly;

              return (
                <article
                  key={plan.name}
                  className={[
                    classes.card,
                    isEnterprise ? classes.cardEnterprise : '',
                    isUnavailable ? classes.cardUnavailable : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-disabled={isUnavailable || undefined}
                >
                  <div className={classes.cardHeader}>
                    <h3 className={classes.planName}>{plan.name}</h3>
                    <div className={classes.priceRow}>
                      <div className={classes.priceLine}>
                        <div className={classes.priceSwap}>
                          <p
                            className={`${classes.price}${
                              isMonthly ? ` ${classes.priceHidden}` : ''
                            }`}
                            aria-hidden={isMonthly || undefined}
                          >
                            {plan.annualPrice}
                            <span className={classes.priceSuffix}>/month</span>
                          </p>
                          <p
                            className={`${classes.price}${
                              isMonthly ? '' : ` ${classes.priceHidden}`
                            }`}
                            aria-hidden={!isMonthly || undefined}
                          >
                            {isEnterprise
                              ? plan.annualPrice
                              : plan.monthlyPrice}
                            <span className={classes.priceSuffix}>/month</span>
                          </p>
                        </div>
                        {!isEnterprise && plan.monthlyHours != null ? (
                          <span
                            className={`${classes.hoursBadge}${
                              isAnnual ? ` ${classes.hoursBadgeAnnual}` : ''
                            }`}
                          >
                            <span
                              className={
                                isAnnual ? classes.hoursStruck : undefined
                              }
                            >
                              max.{' '}
                              <span className={classes.hoursBadgeStrong}>
                                {plan.monthlyHours}
                              </span>{' '}
                              hours
                            </span>
                          </span>
                        ) : null}
                      </div>
                      <p
                        className={`${classes.savingsLabel}${
                          showSavings ? '' : ` ${classes.fadeHidden}`
                        }`}
                        aria-hidden={!showSavings || undefined}
                      >
                        {annualSavings
                          ? `Total savings ${annualSavings}`
                          : 'Total savings'}
                      </p>
                    </div>
                  </div>
                  <p className={classes.description}>{plan.description}</p>
                  <div className={classes.featuresHeadingBlock}>
                    <p className={classes.featuresHeading}>
                      {plan.featuresHeading}
                    </p>
                    {plan.inheritsFrom ? (
                      <p className={classes.inheritsFrom}>
                        <span className={classes.featureIcon}>
                          <InheritArrowIcon />
                        </span>
                        Everything included in {plan.inheritsFrom}, plus...
                      </p>
                    ) : null}
                  </div>
                  <ul className={classes.featureList}>
                    {plan.features.map((feature) => (
                      <li key={feature} className={classes.featureItem}>
                        <span className={classes.featureIcon}>
                          <CheckIcon color={checkColor} />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
          {scrollMetrics.canScroll ? (
            <div
              className={classes.cardsScrollIndicator}
              aria-hidden="true"
            >
              <div className={classes.cardsScrollTrack}>
                <div
                  className={classes.cardsScrollThumb}
                  style={{
                    width: `${scrollMetrics.thumbWidth}%`,
                    left: `${scrollMetrics.thumbOffset}%`,
                  }}
                />
              </div>
            </div>
          ) : null}
          <Typography className={classes.cardsScrollHint} aria-hidden="true">
            Swipe to compare plans
          </Typography>

          <Container maxWidth={false}>
            <Box
              component="section"
              className={classes.maintenanceSection}
              aria-labelledby="maintenance-plan-heading"
            >
              <div className={classes.maintenanceInner}>
                <div className={classes.maintenanceIntro}>
                  <Typography
                    id="maintenance-plan-heading"
                    component="h2"
                    className={classes.maintenanceTitle}
                  >
                    WordPress Care Plan
                  </Typography>
                  <Typography className={classes.maintenancePrice}>
                    $1,500/yr
                  </Typography>
                  <Typography className={classes.maintenanceDescription}>
                    For businesses that want their WordPress site kept secure,
                    stable, updated, and performing reliably, without ongoing
                    design or development needs.
                  </Typography>
                  <Button
                    size="large"
                    variant="outlined"
                    color="secondary"
                    to="/contact/"
                  >
                    Get started
                  </Button>
                </div>
                <div className={classes.maintenanceFeaturesCol}>
                  <ul className={classes.maintenanceFeatureList}>
                    {maintenanceFeatures.map((feature) => (
                      <li
                        key={feature}
                        className={classes.maintenanceFeatureItem}
                      >
                        <span className={classes.maintenanceFeatureIcon}>
                          <CarePlanCheckIcon />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <p className={classes.maintenanceNote}>
                    Additional hosting fees will be applied if you exceed{' '}
                    <a
                      href="https://pantheon.io/pricing-comparison"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.footnoteLink}
                    >
                      Pantheon&apos;s maximum allowed monthly visits
                      <span className={classes.visuallyHidden}>
                        {' '}
                        (opens in a new tab)
                      </span>
                    </a>
                    , have more than one site, or choose a different plan.
                  </p>
                </div>
              </div>
            </Box>
          </Container>
        </VerticalPadding>
      </Box>
      <Box bgcolor={theme.palette.primary.main}>
        <VerticalPadding>
          <Container maxWidth={false}>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Grid item xs={10} md={4}>
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  style={{ color: 'white', fontFamily: 'Visby CF' }}
                >
                  spend a little extra,
                  <br />
                  get a little extra
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  style={{ fontWeight: 500 }}
                >
                  Need a short-term push? Keep your plan—dedicated project
                  capacity can be quoted separately on Essentials through
                  Premium.
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  style={{ fontWeight: 500 }}
                >
                  If your needs change, move tiers for more priority—or go
                  annual to work by queue instead of hourly.
                </Typography>
              </Grid>
              <Grid item xs={12} md={1} className={classes.spacer}></Grid>
              <Grid item md={1} className={classes.vertLine}></Grid>
              <Grid item xs={10} md={4}>
                <Typography
                  variant="h3"
                  component="h2"
                  align="center"
                  gutterBottom
                  style={{ color: 'white', fontFamily: 'Visby CF' }}
                >
                  we&apos;re always open
                  <br />
                  for a chat
                </Typography>
                <Typography
                  component="p"
                  variant="body1"
                  align="center"
                  style={{ fontWeight: 500 }}
                >
                  Not sure which plan fits? We&apos;ll help you choose the right
                  priority level and billing option.
                </Typography>
                <Box textAlign="center">
                  <Button
                    size="large"
                    variant="outlined"
                    style={{ color: 'white' }}
                    to="/contact/"
                  >
                    give us a call
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </VerticalPadding>
      </Box>
      <CallToAction imageSrc="call-to-action-man-couch.jpg">
        <Typography
          variant="h2"
          color="textSecondary"
          gutterBottom={true}
          style={{ maxWidth: '9em' }}
        >
          Get worry-free web operations
        </Typography>
        <Typography
          variant="body2"
          style={{ fontSize: 24, marginTop: 40, marginBottom: 50 }}
        >
          Develop, launch, manage, and iterate digital marketing assets faster.
          And bring advanced digital experiences to your audience more quickly
          and efficiently.
        </Typography>
        <Button
          size="large"
          variant="outlined"
          color="secondary"
          to="/contact/"
        >
          Let&apos;s talk
        </Button>
      </CallToAction>
    </Layout>
  );
};

export default Pricing;
